import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ImageUpload } from "@/components/ui/image-upload";
import { PageHeader } from "@/components/layout/page-header";
import { FormError } from "@/components/form-error";
import { SubmitButton } from "@/components/submit-button";
import { useCreateNotificationMutation } from "@/hooks/use-notifications";
import {
  createNotificationSchema,
  type CreateNotificationFormData,
} from "@/lib/validations/notification";
import { uploadImage } from "@/lib/api/images";
import { useServiceStore } from "@/stores/service-store";

export function NewNotificationPage() {
  const navigate = useNavigate();
  const { selectedServiceId } = useServiceStore();
  const createMutation = useCreateNotificationMutation();
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const form = useForm<CreateNotificationFormData>({
    resolver: zodResolver(createNotificationSchema),
    defaultValues: {
      title: "",
      message: "",
      link: "",
      image: null,
      scheduleOption: "now",
      customDateTime: "",
    },
  });

  // Covers the image upload as well as the create call.
  const { isSubmitting } = form.formState;

  const getMinDateTime = () => {
    const oneHourLater = new Date(Date.now() + 60 * 60 * 1000);
    const year = oneHourLater.getFullYear();
    const month = String(oneHourLater.getMonth() + 1).padStart(2, "0");
    const day = String(oneHourLater.getDate()).padStart(2, "0");
    const hours = String(oneHourLater.getHours()).padStart(2, "0");
    const minutes = String(oneHourLater.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const onSubmit = async (data: CreateNotificationFormData) => {
    try {
      let imageUrl: string | undefined;

      if (data.image && selectedServiceId) {
        setIsUploadingImage(true);
        try {
          const uploadResult = await uploadImage(data.image, { serviceId: selectedServiceId });
          imageUrl = uploadResult.url;
        } catch {
          form.setError("image", {
            message: "Failed to upload image. Please try again.",
          });
          return;
        } finally {
          setIsUploadingImage(false);
        }
      }

      let scheduled_at: string | undefined;

      if (data.scheduleOption === "in_one_hour") {
        const oneHourLater = new Date(Date.now() + 60 * 60 * 1000);
        scheduled_at = oneHourLater.toISOString();
      } else if (data.scheduleOption === "custom" && data.customDateTime) {
        const localDate = new Date(data.customDateTime);
        scheduled_at = localDate.toISOString();
      }

      const payload = {
        payload: {
          title: data.title,
          message: data.message,
          ...(data.link && { link: data.link }),
          ...(imageUrl && { image_url: imageUrl }),
        },
        ...(scheduled_at && { scheduled_at }),
      };

      const response = await createMutation.mutateAsync(payload);
      navigate(`/notifications/${response.data.id}`);
    } catch {
      // Error is handled by mutation state
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6">
      <PageHeader
        breadcrumbs={[{ label: "Notifications", to: "/notifications" }]}
        title="Create Notification"
        actions={
          <SubmitButton
            form="notification-form"
            pending={isSubmitting}
            pendingText="Creating..."
          >
            Create Notification
          </SubmitButton>
        }
      />

      {createMutation.isError && (
        <FormError className="mx-auto w-full max-w-3xl">
          Failed to create notification. Please try again.
        </FormError>
      )}

      <Card className="mx-auto w-full max-w-3xl">
        <CardHeader>
          <CardTitle>Notification Details</CardTitle>
          <CardDescription>
            Fill in the details for your notification.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            id="notification-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="New Message"
                maxLength={50}
                {...form.register("title")}
                aria-invalid={!!form.formState.errors.title}
                aria-describedby={
                  form.formState.errors.title ? "title-error" : undefined
                }
              />
              {form.formState.errors.title && (
                <p
                  id="title-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">
                Message <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="message"
                placeholder="Enter notification message..."
                rows={5}
                maxLength={200}
                {...form.register("message")}
                aria-invalid={!!form.formState.errors.message}
                aria-describedby={
                  form.formState.errors.message ? "message-error" : undefined
                }
              />
              {form.formState.errors.message && (
                <p
                  id="message-error"
                  className="text-[13px] text-destructive"
                  role="alert"
                >
                  {form.formState.errors.message.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduleOption">Send</Label>
              <Select
                value={form.watch("scheduleOption")}
                onValueChange={(value) =>
                  form.setValue(
                    "scheduleOption",
                    value as "now" | "in_one_hour" | "custom"
                  )
                }
              >
                <SelectTrigger id="scheduleOption">
                  <SelectValue placeholder="Select when to send" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="now">Now</SelectItem>
                  <SelectItem value="in_one_hour">+1 hour</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.watch("scheduleOption") === "custom" && (
              <div className="space-y-2">
                <Label htmlFor="customDateTime">Select Date and Time</Label>
                <Input
                  id="customDateTime"
                  type="datetime-local"
                  min={getMinDateTime()}
                  {...form.register("customDateTime")}
                  aria-invalid={!!form.formState.errors.customDateTime}
                  aria-describedby={
                    form.formState.errors.customDateTime
                      ? "customDateTime-error"
                      : undefined
                  }
                />
                <p className="text-[13px] text-muted-foreground">
                  Your local time will be converted to UTC
                </p>
                {form.formState.errors.customDateTime && (
                  <p
                    id="customDateTime-error"
                    className="text-[13px] text-destructive"
                    role="alert"
                  >
                    {form.formState.errors.customDateTime.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="link">Link (optional)</Label>
              <Input
                id="link"
                type="url"
                placeholder="https://example.com/details"
                {...form.register("link")}
                aria-invalid={!!form.formState.errors.link}
                aria-describedby={
                  form.formState.errors.link ? "link-error" : undefined
                }
              />
              {form.formState.errors.link && (
                <p id="link-error" className="text-[13px] text-destructive" role="alert">
                  {form.formState.errors.link.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Image (optional)</Label>
              <Controller
                name="image"
                control={form.control}
                render={({ field }) => (
                  <ImageUpload
                    value={field.value ?? null}
                    onChange={field.onChange}
                    error={form.formState.errors.image?.message}
                    disabled={isSubmitting}
                    isUploading={isUploadingImage}
                  />
                )}
              />
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
